import { useContext, useEffect } from 'react';

import {
  type BoxGroup,
  BoxPlot,
  FormatType,
  getOtherColor,
  getPrimaryColor,
  useChartTheme,
} from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import type { BreakdownBoxDataSet } from '../DataProvider/types';
import { useChart } from './ChartProvider';
import { PageExportContext } from './chart-menu/PageExportProvider';
import { SharableChart } from './chart-menu/plot-share/SharableChart';
import {
  isParameterSelectionEqualToEntity,
  type ParameterSelection,
} from './TextChart/utils/resolve-entity-utils';
import { getActiveColor, useActiveColors } from './utils/useActiveColors';

type BoxProps = {
  data: BreakdownBoxDataSet['data'];
  primaryParameterValues?: ParameterSelection[];
};

const resolveEntityColors = (
  boxData: BoxGroup[],
  palette: readonly string[]
): { entity: string; color: string }[] => {
  const colors: { entity: string; color: string }[] = [];
  for (const box of boxData) {
    const entity = box.secondaryDimension ?? box.dimension;
    if (colors.some((c) => c.entity === entity)) continue;
    colors.push({
      entity,
      color:
        box.color ??
        palette[colors.length % palette.length] ??
        palette[0] ??
        '',
    });
  }
  return colors;
};

export const Box = ({ data, primaryParameterValues }: BoxProps) => {
  const { item } = useChart();
  const colorPalette = item?.plot?.colorPalette;
  const { code } = useCurrencyPreference();
  const { colorMap } = useActiveColors();
  const { palette } = useChartTheme();
  const pageExport = useContext(PageExportContext);

  const boxData: BoxGroup[] = data.flatMap((primaryD) =>
    primaryD.breakdown.map((secondaryD): BoxGroup => {
      const dimension = secondaryD.dim;
      const entityRcid = secondaryD.id ?? null;
      const isPrimary =
        primaryParameterValues?.some((p) =>
          isParameterSelectionEqualToEntity(p, dimension, entityRcid)
        ) ?? false;
      const activeColor = getActiveColor({
        colorMap,
        dimension,
        id: entityRcid,
      });
      const fallbackColor = (() => {
        if (!primaryParameterValues?.length) return undefined;
        return isPrimary ? getPrimaryColor() : getOtherColor();
      })();
      const color = activeColor ?? fallbackColor;

      return {
        dimension: primaryD.dim,
        secondaryDimension: secondaryD.dim,
        boxData: {
          percentile10: secondaryD.box.q10,
          ...(secondaryD.box.q25 != null && {
            percentile25: secondaryD.box.q25,
          }),
          percentile50: secondaryD.box.q50,
          ...(secondaryD.box.q75 != null && {
            percentile75: secondaryD.box.q75,
          }),
          percentile90: secondaryD.box.q90,
        },
        ...(color && { color }),
      };
    })
  );

  const entityColors = resolveEntityColors(
    boxData,
    colorPalette?.length ? colorPalette : palette
  );
  const entityColorsKey = JSON.stringify(entityColors);
  const chartId = item?.id;
  const updateChartExportInfo = pageExport?.updateChartExportInfo;
  // biome-ignore lint/correctness/useExhaustiveDependencies: entityColorsKey stands in for entityColors
  useEffect(() => {
    if (!chartId || !updateChartExportInfo) return;
    updateChartExportInfo(chartId, { boxEntityColors: entityColors });
  }, [chartId, entityColorsKey, updateChartExportInfo]);

  return (
    <SharableChart
      Component={BoxPlot}
      data={boxData}
      format={FormatType.CURRENCY}
      currencyCode={code}
      colorPalette={colorPalette}
      showQuartiles={item?.plot?.showQuartiles ?? false}
    />
  );
};
